import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import {
  BUSINESS_TYPE_OPTIONS,
  DAILY_INQUIRY_OPTIONS,
  DISCOVERY_OPTIONS,
  getOptionLabel,
  GOAL_INTENT_OPTIONS,
  LEAD_TIER_KEYS,
  MONTHLY_SALES_OPTIONS,
  PREFERRED_LANGUAGE_OPTIONS,
  PRIMARY_PROBLEM_OPTIONS,
} from 'views/waitlist/waitlistOptions';

const STATUS_VARIANTS = {
  pending: 'warning',
  under_review: 'info',
  approved: 'success',
  rejected: 'danger',
  invited: 'primary',
  converted: 'success',
  archived: 'secondary',
};

const WAITLIST_STATUSES = [
  'pending',
  'under_review',
  'approved',
  'rejected',
  'invited',
  'converted',
  'archived',
];

const getStatusLabel = (t, status) => {
  const key = `SUPERADMIN.WAITLIST_TAB.STATUS_${String(status || '').toUpperCase()}`;
  const translated = t(key);
  return translated === key ? String(status || '').replace('_', ' ') : translated;
};

const SalesWaitlistPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ingestedFilter, setIngestedFilter] = useState('false');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (ingestedFilter) params.ingested = ingestedFilter;
      const response = await axiosInstance.get('sales/waitlist/', { params });
      setEntries(response.data?.entries || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_WAITLIST'));
    } finally {
      setLoading(false);
    }
  }, [ingestedFilter, search, statusFilter, t]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleReview = async entryId => {
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.get(`sales/waitlist/${entryId}/`);
      setSelected(response.data?.entry || null);
      setShowModal(true);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_WAITLIST'));
    }
  };

  const handleIngest = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/waitlist/${selected.id}/ingest/`);
      const opportunityId = response.data?.opportunity?.id;
      setShowModal(false);
      setSelected(null);
      setSuccess(t('SALES.WAITLIST_INGEST_SUCCESS', { name: `${selected.first_name} ${selected.last_name}`.trim() }));
      await loadEntries();
      if (opportunityId) {
        navigate(`/sales/opportunity/${opportunityId}`);
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_INGEST_WAITLIST'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.WAITLIST')} className="mb-3" />

      <Card className="mb-3 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-primary fw-semi-bold">{t('SALES.WAITLIST_PANEL')}</div>
            <div className="text-600 fs--1">{t('SALES.WAITLIST_PANEL_HELP')}</div>
          </div>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card className="mb-3">
        <Card.Body>
          <Form
            onSubmit={event => {
              event.preventDefault();
              loadEntries();
            }}
          >
            <Row className="g-3">
              <Col lg={6}>
                <InputGroup>
                  <InputGroup.Text>{t('SALES.SEARCH')}</InputGroup.Text>
                  <Form.Control
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={t('SALES.WAITLIST_SEARCH_PLACEHOLDER')}
                  />
                </InputGroup>
              </Col>
              <Col lg={3}>
                <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                  <option value="">{t('SUPERADMIN.WAITLIST_TAB.ALL_STATUSES')}</option>
                  {WAITLIST_STATUSES.map(status => (
                    <option key={status} value={status}>
                      {getStatusLabel(t, status)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3}>
                <Form.Select value={ingestedFilter} onChange={event => setIngestedFilter(event.target.value)}>
                  <option value="false">{t('SALES.WAITLIST_PENDING_INTAKE')}</option>
                  <option value="">{t('SALES.WAITLIST_ALL_INTAKE_STATES')}</option>
                  <option value="true">{t('SALES.WAITLIST_ALREADY_IN_PIPELINE')}</option>
                </Form.Select>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.NAME')}</th>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.EMAIL')}</th>
                  <th>{t('SALES.WAITLIST_COMPANY')}</th>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.BUSINESS_TYPE')}</th>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.LEAD_TIER')}</th>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.STATUS')}</th>
                  <th>{t('SALES.WAITLIST_PIPELINE_STATUS')}</th>
                  <th>{t('SUPERADMIN.WAITLIST_TAB.DATE')}</th>
                  <th className="text-end">{t('SUPERADMIN.WAITLIST_TAB.ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.length ? entries.map(entry => (
                  <tr key={entry.id}>
                    <td className="fw-semi-bold">{entry.first_name} {entry.last_name}</td>
                    <td>{entry.email}</td>
                    <td>{entry.company_name || entry.business_document || '—'}</td>
                    <td>{getOptionLabel(BUSINESS_TYPE_OPTIONS, entry.business_type, t)}</td>
                    <td>
                      <Badge bg={entry.lead_tier === 'ideal' ? 'success' : entry.lead_tier === 'medium' ? 'warning' : 'secondary'}>
                        {t(LEAD_TIER_KEYS[entry.lead_tier] || 'SUPERADMIN.WAITLIST_TAB.LEAD_TIER_LOW')}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={STATUS_VARIANTS[entry.status] || 'secondary'}>
                        {getStatusLabel(t, entry.status)}
                      </Badge>
                    </td>
                    <td>
                      {entry.sales_opportunity_id ? (
                        <Badge bg="success">{t('SALES.WAITLIST_ALREADY_IN_PIPELINE')}</Badge>
                      ) : (
                        <span className="text-600">{t('SALES.WAITLIST_PENDING_INTAKE')}</span>
                      )}
                    </td>
                    <td>{entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}</td>
                    <td className="text-end d-flex justify-content-end gap-2">
                      {entry.sales_opportunity_id ? (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => navigate(`/sales/opportunity/${entry.sales_opportunity_id}`)}
                        >
                          {t('SALES.WAITLIST_OPEN_OPPORTUNITY')}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline-primary" onClick={() => handleReview(entry.id)}>
                        {t('SUPERADMIN.WAITLIST_TAB.REVIEW')}
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={9} className="text-center text-600 py-4">{t('SALES.WAITLIST_EMPTY')}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t('SUPERADMIN.WAITLIST_TAB.MODAL_TITLE', { name: `${selected?.first_name || ''} ${selected?.last_name || ''}`.trim() })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <h6>{t('SUPERADMIN.WAITLIST_TAB.BASIC_DATA')}</h6>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.EMAIL')}:</strong> {selected.email}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.WHATSAPP')}:</strong> {selected.phone || '—'}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.COUNTRY')}:</strong> {selected.country || '—'}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.STATE')}:</strong> {selected.city || '—'}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.LANGUAGE')}:</strong> {getOptionLabel(PREFERRED_LANGUAGE_OPTIONS, selected.preferred_language, t)}</p>
                </Col>
                <Col md={6}>
                  <h6>{t('SUPERADMIN.WAITLIST_TAB.BUSINESS')}</h6>
                  <p className="mb-1"><strong>{t('SALES.WAITLIST_COMPANY')}:</strong> {selected.company_name || selected.business_document || '—'}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.BUSINESS_TYPE')}:</strong> {getOptionLabel(BUSINESS_TYPE_OPTIONS, selected.business_type, t)}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.SALES_RANGE')}:</strong> {getOptionLabel(MONTHLY_SALES_OPTIONS, selected.ml_monthly_sales_range, t)}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.DAILY_INQUIRIES')}:</strong> {getOptionLabel(DAILY_INQUIRY_OPTIONS, selected.daily_inquiry_volume, t)}</p>
                  <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.LEAD_TIER')}:</strong> {t(LEAD_TIER_KEYS[selected.lead_tier] || 'SUPERADMIN.WAITLIST_TAB.LEAD_TIER_LOW')}</p>
                </Col>
              </Row>

              <h6>{t('SUPERADMIN.WAITLIST_TAB.COMMERCIAL_FIT')}</h6>
              <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.PRIMARY_PROBLEM')}:</strong> {getOptionLabel(PRIMARY_PROBLEM_OPTIONS, selected.primary_problem, t)}</p>
              <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.GOAL_INTENT')}:</strong> {getOptionLabel(GOAL_INTENT_OPTIONS, selected.goal_intent, t)}</p>
              <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.SAMPLE_CUSTOMER_QUESTION')}:</strong> {selected.sample_customer_question || '—'}</p>

              <h6 className="mt-3">{t('SUPERADMIN.WAITLIST_TAB.DISCOVERY')}</h6>
              <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.SOURCE')}:</strong> {getOptionLabel(DISCOVERY_OPTIONS, selected.discovery_source, t)}</p>
              {selected.referral_name ? <p className="mb-1"><strong>{t('SUPERADMIN.WAITLIST_TAB.REFERRAL')}:</strong> {selected.referral_name}</p> : null}

              {selected.sales_opportunity_id ? (
                <Alert variant="success" className="mt-3 mb-0 d-flex justify-content-between align-items-center">
                  <span>{t('SALES.WAITLIST_ALREADY_IN_PIPELINE')}</span>
                  <Button size="sm" variant="outline-success" onClick={() => navigate(`/sales/opportunity/${selected.sales_opportunity_id}`)}>
                    {t('SALES.WAITLIST_OPEN_OPPORTUNITY')}
                  </Button>
                </Alert>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={() => setShowModal(false)}>
            {t('SUPERADMIN.WAITLIST_TAB.CANCEL')}
          </Button>
          {!selected?.sales_opportunity_id ? (
            <Button variant="primary" onClick={handleIngest} disabled={saving}>
              {saving ? t('SUPERADMIN.WAITLIST_TAB.SAVING') : t('SALES.WAITLIST_ADD_TO_PIPELINE')}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SalesWaitlistPage;
